// Drawer with one alarm: video (live / at alarm time), media, extracted vendor fields,
// and the inbound message metadata (raw .eml for admins).

import { useEffect, useRef } from 'react';
import { api, downloadAuthed, type AlarmDetail, type Json } from '../api.ts';
import { useAction, useLoad } from '../hooks.ts';
import { href } from '../router.ts';
import { useSession } from '../session.tsx';
import { fmtBytes, siteAndLocal } from '../time.ts';
import { AuthImg, AuthVideo } from './AuthMedia.tsx';
import { Icon } from './Icon.tsx';
import { VideoPlayer } from './VideoPlayer.tsx';
import { ErrorBox, Flags, InlineError, Loading, StatusPill } from './ui.tsx';

function T({ iso, tz }: { iso: string | null | undefined; tz: string | null | undefined }) {
  const t = siteAndLocal(iso, tz);
  return (
    <span className="mono" title={t.local}>
      {t.site}
    </span>
  );
}

function valueText(v: Json): string {
  if (v === null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function Body({ a }: { a: AlarmDetail }) {
  const s = useSession();
  const dl = useAction(downloadAuthed);
  const tz = a.siteTimeZone;
  const snapshots = a.media.filter((m) => m.kind === 'snapshot');
  const clips = a.media.filter((m) => m.kind === 'clip');
  const exports = a.media.filter((m) => m.kind === 'archive_export');
  const vendorEntries = a.vendorPayload ? Object.entries(a.vendorPayload) : [];

  return (
    <div className="db">
      <div className="kv">
        <div>
          <span className="lbl">Occurred (site)</span>
          <T iso={a.occurredAt} tz={tz} />
        </div>
        <div>
          <span className="lbl">Received</span>
          <T iso={a.receivedAt} tz={tz} />
        </div>
        <div>
          <span className="lbl">Latency</span>
          <b className={`mono ${a.latencyMs !== null && a.latencyMs > 2000 ? 'warn' : ''}`}>{a.latencyMs === null ? '—' : `${a.latencyMs} ms`}</b>
        </div>
        <div>
          <span className="lbl">Confidence</span>
          <b className="mono">{a.vendorConfidence === null ? '—' : a.vendorConfidence}</b>
        </div>
        <div>
          <span className="lbl">Burst</span>
          <b className="mono">×{a.burstCount}</b>
        </div>
      </div>
      <div className="row-wrap">
        <Flags flags={a.flags} />
      </div>

      <section className="sec" aria-label="Video">
        <div className="lbl">Video</div>
        {a.cameraId ? (
          <VideoPlayer cameraId={a.cameraId} alarmAt={a.occurredAt} />
        ) : (
          <div className="state empty">No mapped camera, so there is no video for this alarm.</div>
        )}
      </section>

      <section className="sec" aria-label="Media">
        <div className="lbl">Media from the alarm email</div>
        {a.media.length === 0 && <div className="note">No snapshot or clip was attached.</div>}
        {snapshots.length > 0 && (
          <div className="snaps">
            {snapshots.map((m) => (
              <figure key={m.id}>
                <AuthImg url={m.url} alt={`Snapshot ${m.fileName ?? m.id}`} />
                <figcaption className="note mono">
                  {m.fileName ?? m.id} · {fmtBytes(m.size)}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
        {clips.map((m) => (
          <figure key={m.id}>
            <AuthVideo url={m.url} contentType={m.contentType} label={`Clip ${m.fileName ?? m.id}`} />
            <figcaption className="note mono">
              {m.fileName ?? m.id} · {m.contentType} · {fmtBytes(m.size)}
            </figcaption>
          </figure>
        ))}
        {exports.map((m) => (
          <button key={m.id} type="button" className="btn" onClick={() => void dl.run(m.url, m.fileName ?? `${m.id}`)}>
            <Icon name="download" />
            Archive export · {m.fileName ?? m.id} · {fmtBytes(m.size)}
          </button>
        ))}
      </section>

      <section className="sec" aria-label="Vendor fields">
        <div className="lbl">Extracted vendor fields{a.vendor ? ` · ${a.vendor}` : ''}</div>
        {vendorEntries.length === 0 ? (
          <div className="note">No fields were extracted.</div>
        ) : (
          <div className="tw">
            <table className="kvt">
              <tbody>
                {vendorEntries.map(([k, v]) => (
                  <tr key={k}>
                    <th scope="row">{k}</th>
                    <td className="mono">{valueText(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="sec" aria-label="Inbound message">
        <div className="lbl">Inbound message</div>
        {!a.message ? (
          <div className="note">No inbound message is linked.</div>
        ) : (
          <>
            <div className="tw">
              <table className="kvt">
                <tbody>
                  <tr>
                    <th scope="row">Message ID</th>
                    <td className="mono">{a.message.id}</td>
                  </tr>
                  <tr>
                    <th scope="row">To</th>
                    <td className="mono">{a.message.rcptTo}</td>
                  </tr>
                  <tr>
                    <th scope="row">From</th>
                    <td className="mono">{a.message.smtpFrom}</td>
                  </tr>
                  <tr>
                    <th scope="row">Received</th>
                    <td>
                      <T iso={a.message.receivedAt} tz={tz} />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Parse status</th>
                    <td>
                      <StatusPill status={a.message.parseStatus} />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Template</th>
                    <td className="mono">
                      {a.message.parserTemplate ? `${a.message.parserTemplate.vendor} v${a.message.parserTemplate.version}` : '—'}
                    </td>
                  </tr>
                  {a.message.parseError && (
                    <tr>
                      <th scope="row">Parse error</th>
                      <td className="mono warn-t">{a.message.parseError}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {s.isAdmin && (
              <div className="row-wrap">
                {a.message.rawUrl && (
                  <button type="button" className="btn" disabled={dl.busy} onClick={() => a.message?.rawUrl && void dl.run(a.message.rawUrl, `${a.message.id}.eml`)}>
                    <Icon name="download" />
                    Raw .eml
                  </button>
                )}
                <a className="btn" href={href.ingest()}>
                  <Icon name="mail" />
                  Alarm ingest
                </a>
                <InlineError error={dl.error} />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

export function AlarmDetailPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const load = useLoad((signal) => api.alarm(id, signal), [id]);
  const a = load.data;
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, [onClose]);

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer wide" role="dialog" aria-modal="true" aria-labelledby="alarm-detail-h">
        <div className="dh">
          <Icon name="bolt" />
          <div className="dh-t">
            <h3 id="alarm-detail-h">
              {a ? (
                <>
                  <span className="mono dim">#{a.displayNo ?? a.id}</span> {a.analytic ?? 'Alarm'}
                </>
              ) : (
                'Alarm'
              )}
            </h3>
            {a && (
              <div className="note">
                {a.siteId ? <a href={href.site(a.siteId)}>{a.siteName ?? a.siteId}</a> : <span className="warn-t">Unknown site</span>}
                {' · '}
                {a.cameraName ?? <span className="warn-t">Unmapped camera</span>}
                {a.siteTimeZone ? ` · ${a.siteTimeZone}` : ''}
              </div>
            )}
          </div>
          <button ref={closeRef} type="button" className="btn icon" onClick={onClose} aria-label="Close alarm detail">
            <Icon name="x" />
          </button>
        </div>
        {load.error && !a ? (
          <div className="db">
            <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the alarm" />
          </div>
        ) : !a ? (
          <div className="db">
            <Loading label="Loading alarm…" />
          </div>
        ) : (
          <Body a={a} />
        )}
      </aside>
    </div>
  );
}

